import { useRef, useEffect, useState } from "react"
import { animate, motion, useInView, useMotionValue, useScroll, useTransform } from "framer-motion"
import { Link } from "react-router-dom"
import {
  ArrowRight,
  Flame,
  Thermometer,
  Zap,
  Wind,
  Droplets,
  FlaskConical,
  Snowflake,
  Building2,
  Handshake,
  Clock3,
  Wrench,
  Sparkles,
  Boxes,
  Gauge,
  Target,
  ShieldCheck,
  Radar,
  ArrowUpRight,
  CheckCircle2,
  type LucideIcon,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { CTASection } from "@/components/sections/shared/CTASection"
import { TrustStrip } from "@/components/sections/shared/TrustStrip"
import { fadeInUp, staggerContainer } from "@/lib/animations"
import { useReducedMotion } from "@/hooks/useReducedMotion"
import { ibrApproval, thermaxAssociation } from "@/data/credentials"

const offerings = [
  {
    name: "Boiler & Heater",
    description: "Steam and thermic systems for high-duty operations.",
    icon: Flame,
    accentClass: "text-[#B34734]",
    surfaceClass: "from-[#F8EEEA] to-[#F4E3DE]",
  },
  {
    name: "Process Heating",
    description: "Application-specific process heat architecture.",
    icon: Thermometer,
    accentClass: "text-[#BA5D33]",
    surfaceClass: "from-[#FAF0E8] to-[#F3E3D8]",
  },
  {
    name: "Energy Solutions",
    description: "Integrated utilities for optimized energy use.",
    icon: Zap,
    accentClass: "text-[#8E6E2F]",
    surfaceClass: "from-[#F8F4E7] to-[#F2EAD3]",
  },
  {
    name: "Air Pollution Control",
    description: "Emission-control systems aligned to compliance goals.",
    icon: Wind,
    accentClass: "text-[#3F7E62]",
    surfaceClass: "from-[#EDF5EF] to-[#DFEEE3]",
  },
  {
    name: "Water & Waste Solutions",
    description: "Treatment and reuse systems for responsible operations.",
    icon: Droplets,
    accentClass: "text-[#2F7286]",
    surfaceClass: "from-[#EAF3F6] to-[#DAEBF1]",
  },
  {
    name: "Chemicals",
    description: "Specialty formulations for industrial performance.",
    icon: FlaskConical,
    accentClass: "text-[#4D699D]",
    surfaceClass: "from-[#EEF1F8] to-[#DFE5F3]",
  },
  {
    name: "Process Cooling",
    description: "Reliable cooling loops for production continuity.",
    icon: Snowflake,
    accentClass: "text-[#3B6B9E]",
    surfaceClass: "from-[#EDF2F8] to-[#DFE8F4]",
  },
  {
    name: "Absorption Cooling",
    description: "Efficient thermal cooling for utility-intensive sites.",
    icon: Building2,
    accentClass: "text-[#4B618D]",
    surfaceClass: "from-[#EEF2F6] to-[#E1E8F0]",
  },
]

const whyChooseUs = [
  {
    title: "Trusted Partner",
    description: "Official Thermax Channel Associate with proven expertise",
    icon: Handshake,
  },
  {
    title: "On-Time Delivery",
    description: "Committed to meeting project timelines every time",
    icon: Clock3,
  },
  {
    title: "Regulatory Readiness",
    description: "IBR Class I recognized capability for boiler and steam pipeline works",
    icon: ShieldCheck,
  },
  {
    title: "Turnkey Projects",
    description: "End-to-end custom built systems",
    icon: Wrench,
  },
  {
    title: "Precision Workmanship",
    description: "Flawless fabrication and build quality",
    icon: Sparkles,
  },
  {
    title: "Comprehensive Offerings",
    description: "Boilers, water treatment, chemicals - all needs met",
    icon: Boxes,
  },
]

const performanceFocus: Array<{
  label: string
  value: string
  icon: LucideIcon
  tone: "high" | "lifecycle"
}> = [
  {
    label: "Execution Reliability",
    value: "High",
    icon: Gauge,
    tone: "high",
  },
  {
    label: "Technical Precision",
    value: "High",
    icon: Target,
    tone: "high",
  },
  {
    label: "Lifecycle Support",
    value: "End-to-End",
    icon: ShieldCheck,
    tone: "lifecycle",
  },
]

const stats = [
  { value: 200, label: "Projects Completed", suffix: "+" },
  { value: 35, label: "Years Experience", suffix: "+" },
  { value: 96, label: "On-Time Delivery", suffix: "%+" },
  { value: 4, label: "Avg Response Time", prefix: "<", suffix: " hrs" },
]

type StatCounterProps = {
  value: number
  prefix?: string
  suffix?: string
  animateValue: boolean
}

function StatCounter({ value, prefix = "", suffix = "", animateValue }: StatCounterProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true, margin: "-80px" })
  const count = useMotionValue(0)
  const [displayValue, setDisplayValue] = useState(0)

  useEffect(() => {
    const unsubscribe = count.on("change", (latest) => {
      setDisplayValue(Math.round(latest))
    })

    return unsubscribe
  }, [count])

  useEffect(() => {
    if (!isInView) return

    if (!animateValue) {
      count.set(value)
      return
    }

    const controls = animate(count, value, {
      duration: 1.8,
      ease: "easeOut",
    })

    return () => controls.stop()
  }, [animateValue, count, isInView, value])

  return (
    <span ref={ref}>
      {prefix}
      {displayValue}
      {suffix}
    </span>
  )
}

export function Hero() {
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const prefersReducedMotion = useReducedMotion()

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  })

  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1])
  const y = useTransform(scrollYProgress, [0, 0.5], [0, 50])

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.75
    }
  }, [])

  return (
    <section ref={containerRef} className="relative h-screen min-h-[600px] overflow-hidden">
      {/* Ambient glow effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[#9F3A2A]/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-[#2F7F92]/20 rounded-full blur-[120px]" />
      </div>

      <motion.div style={{ scale }} className="absolute inset-0">
        {prefersReducedMotion ? (
          <div className="w-full h-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700" />
        ) : (
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            className="w-full h-full object-cover"
          >
            <source src="/videos/factory_bg.mp4" type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/72 via-slate-900/58 to-slate-900/40" />
        {/* Pattern overlay */}
        <div className="absolute inset-0 pattern-dots opacity-30" />
      </motion.div>

      <motion.div
        style={{ opacity, y }}
        className="relative h-full container mx-auto px-4 flex items-center"
      >
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="max-w-2xl"
        >
          <motion.div variants={fadeInUp} className="mb-4 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/35 bg-slate-900/35 px-3 py-1 text-xs font-semibold uppercase tracking-[0.06em] text-white/95">
              <Handshake className="h-3.5 w-3.5" />
              {thermaxAssociation.label}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/35 bg-slate-900/35 px-3 py-1 text-xs font-semibold uppercase tracking-[0.06em] text-white/95">
              <ShieldCheck className="h-3.5 w-3.5" />
              {ibrApproval.label}
            </span>
          </motion.div>
          <motion.p
            variants={fadeInUp}
            className="text-white/90 font-semibold text-sm md:text-base tracking-wider uppercase mb-4 drop-shadow-[0_6px_18px_rgba(2,6,23,0.45)]"
          >
            Sustainable Solutions for
          </motion.p>
          <motion.h1
            variants={fadeInUp}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 drop-shadow-[0_10px_28px_rgba(2,6,23,0.5)]"
          >
            Energy & <span className="text-gradient-red">Environment</span>
          </motion.h1>
          <motion.p
            variants={fadeInUp}
            className="text-slate-100 text-lg md:text-xl mb-8 max-w-xl drop-shadow-[0_6px_20px_rgba(2,6,23,0.45)]"
          >
            MaxiTherm is your one-stop utility partner for industrial and commercial segments,
            helping customers achieve better resource productivity while maintaining a better
            environment.
          </motion.p>
          <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
            <Button
              variant="cta"
              size="xl"
              className="bg-[#B34734] hover:bg-[#A44030] shadow-black/35 btn-glow group"
              asChild
            >
              <Link to="/projects">
                View Our Projects
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
            <Button
              variant="outline"
              size="xl"
              className="border-white/45 bg-transparent text-white hover:bg-white/15 hover:text-white btn-magnetic"
              asChild
            >
              <Link to="/products">Explore Products</Link>
            </Button>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Enhanced scroll indicator */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="group cursor-pointer"
        >
          <div className="w-6 h-10 border-2 border-white/35 rounded-full flex justify-center pt-2 group-hover:border-white/60 transition-colors">
            <motion.div className="w-1.5 h-3 bg-white/70 rounded-full" />
          </div>
        </motion.div>
      </motion.div>
    </section>
  )
}

export function Offerings() {
  return (
    <section className="section-padding bg-slate-50 relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute -top-20 right-0 h-64 w-64 rounded-full bg-[#DDEAF6] blur-3xl opacity-60" />
      <div className="absolute bottom-0 left-0 h-48 w-48 rounded-full bg-[#F8EEEA] blur-3xl opacity-40" />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
            What We <span className="text-gradient-red">Offer</span>
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Comprehensive solutions for all your industrial heating, cooling, and environmental
            needs
          </p>
        </motion.div>

        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6"
        >
          {offerings.map((item, index) => {
            const Icon = item.icon as LucideIcon

            return (
              <motion.div
                key={item.name}
                variants={fadeInUp}
                className="md:col-span-6 xl:col-span-3 group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 md:p-7 transition-all hover:border-slate-300 card-hover-lift card-shine"
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-maxi-red/75 via-maxi-cyan/65 to-maxi-navy/75 origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100" />
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div
                      className={`inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${item.surfaceClass}`}
                    >
                      <Icon className={`h-6 w-6 ${item.accentClass}`} />
                    </div>
                    <h3 className="mt-4 text-lg font-semibold text-slate-900 group-hover:text-[#9F3A2A] transition-colors">
                      {item.name}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">
                      {item.description}
                    </p>
                  </div>
                  <span className="text-xs font-medium text-slate-400">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
              </motion.div>
            )
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-10"
        >
          <Button variant="cta" size="lg" className="btn-glow" asChild>
            <Link to="/products">
              Explore All Products
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  )
}

export function WhyChooseUs() {
  return (
    <section className="section-padding bg-white relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 pattern-grid opacity-50" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-12 lg:items-stretch">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-4 lg:sticky lg:top-24 flex h-full flex-col"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
              Why Choose <span className="text-gradient-red">Maxitherm</span>
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">
              Decades of industrial execution, engineering rigor, and disciplined delivery make us a
              dependable long-term utility partner.
            </p>
            <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 mt-auto glass-card">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-white">
                  <Radar className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Performance Focus
                  </p>
                  <p className="text-xs text-slate-500">Capability snapshot</p>
                </div>
              </div>

              <div className="space-y-2.5">
                {performanceFocus.map((metric) => {
                  const MetricIcon = metric.icon

                  return (
                    <div
                      key={metric.label}
                      className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5"
                    >
                      <div className="flex min-w-0 items-center gap-2.5">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
                          <MetricIcon className="h-4 w-4" />
                        </div>
                        <span className="truncate text-sm text-slate-700">{metric.label}</span>
                      </div>
                      <span
                        className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                          metric.tone === "high"
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border-sky-200 bg-sky-50 text-sky-700"
                        }`}
                      >
                        {metric.tone === "high" ? (
                          <ArrowUpRight className="h-3.5 w-3.5" />
                        ) : (
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        )}
                        {metric.value}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="lg:col-span-8 lg:self-center grid h-full grid-cols-1 gap-4 md:grid-cols-2 lg:auto-rows-fr"
          >
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon as LucideIcon

              return (
                <motion.div
                  key={item.title}
                  variants={fadeInUp}
                  initial={{ opacity: 0, x: index % 2 === 0 ? 20 : -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: index * 0.06 }}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 md:p-6 h-full min-h-[168px] flex flex-col justify-center card-hover-lift"
                >
                  <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-maxi-red/80 via-maxi-cyan/70 to-maxi-navy/80 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition-colors group-hover:bg-slate-900 group-hover:text-white">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-4">
                        <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
                        <span className="text-xs font-medium text-slate-400">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export function Stats() {
  const prefersReducedMotion = useReducedMotion()

  return (
    <section className="py-16 bg-gradient-to-r from-maxi-navy via-[#2A6EA8] to-maxi-cyan relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-white/10 rounded-full blur-[80px]" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[#9F3A2A]/20 rounded-full blur-[80px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          {stats.map((stat, index) => (
            <motion.div key={stat.label} variants={fadeInUp} custom={index} className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2 drop-shadow-lg">
                <StatCounter
                  value={stat.value}
                  prefix={stat.prefix}
                  suffix={stat.suffix}
                  animateValue={!prefersReducedMotion}
                />
              </div>
              <div className="text-white/80 text-sm md:text-base">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export function TrustSection() {
  return (
    <TrustStrip
      title="Technology + Compliance Backbone"
      description="We combine principal-backed engineering and regulatory capability for steam and utility projects."
      partnerName={thermaxAssociation.label}
      partnerDescription={`As an ${thermaxAssociation.summary}, we pair proven Thermax technology with ${ibrApproval.label} capability for ${ibrApproval.scope}. ${ibrApproval.verificationNote}`}
      ctaLabel="View Approvals"
      ctaHref="/about#approvals"
      ctaExternal={false}
    />
  )
}

export function CTABanner() {
  return (
    <CTASection
      title="Ready to Optimize Your Operations?"
      description="Get in touch with our experts for a free consultation and customized solutions"
      primaryLabel="Get a Quote"
      primaryHref="https://wa.me/919404679370"
      secondaryLabel="Call: +91 94046 79370"
      secondaryHref="tel:+919404679370"
      showPattern
    />
  )
}
